import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { dashboardApiClient } from '@/services/api'
import { HTTP_TIMEOUT_MS } from '@/config'
import {
  FormSection,
  Button,
  Input,
  TextArea,
  ImageUploadModal,
  LoadingSpinner,
  SettingsSaveBar,
  useDirtyForm,
  useToastActions,
  AccountSettingsCard,
} from '@airclou/ui'
import { normalizeRoleForDisplay, getServiceApiUrl, getServiceUrl, FileUploadErrorCode } from '@airclou/utils'
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  Shield,
  RefreshCw,
  Upload,
  Trash2,
  AlertCircle,
  Check,
} from 'lucide-react'

type ProfileForm = {
  firstName: string
  lastName: string
  email: string
  phone: string
  location: string
  title: string
  department: string
  bio: string
  profilePhoto: string
}

const EMPTY_PROFILE: ProfileForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  location: '',
  title: '',
  department: '',
  bio: '',
  profilePhoto: '',
}

export function ProfileTab() {
  const { t } = useTranslation('dashboard')
  const { user, refreshUser, isLoading: isAuthLoading } = useAuth()
  const { success: toastSuccess, error: toastError } = useToastActions()

  // Loading/saving state
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  // True when the profile fetch failed and the form is showing context-only
  // fallback values. Saving from that state would write the blanks over the
  // stored profile, so saving stays blocked until a reload succeeds.
  const [loadFailed, setLoadFailed] = useState(false)

  // Profile state. The baseline is the last state we know the server has, so
  // Discard restores it and editing a field back to its original value
  // correctly stops reporting the form as dirty.
  const form = useDirtyForm<ProfileForm>(EMPTY_PROFILE)
  const profile = form.values
  const hasUnsavedChanges = form.isDirty

  // Image upload modal
  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false)
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null)
  const [photoUploadSuccess, setPhotoUploadSuccess] = useState(false)
  const [photoRemoveSuccess, setPhotoRemoveSuccess] = useState(false)

  // Refresh button state (role section)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Identifiers for the read-only block at the foot of the form. The dashboard
  // session carries no company or workspace — it is a tenant-level console — so
  // those two rows are hidden rather than left reading N/A.
  //
  // `orgRole` is the role from `tenant_users` that `requireTenantRole` resolves
  // — the only one that ever says 'owner'. `user.role` is the dashboard
  // app-access role, which signup writes as 'admin' for everyone, so showing
  // that here told an owner they were an admin, and pasted the same into
  // support tickets.
  //
  // There is deliberately no fallback to `user.role`: answering "Admin" to
  // "what am I?" because a request blipped is a confident wrong answer about
  // the reader's own permissions.
  //
  // Four states, because "no role shown" has two different causes. A string is
  // the answer. `null` is also an answer — the context call came back and this
  // account has no `tenant_users` row, which real provisioning paths produce —
  // so it gets its own line and no retry prompt; telling those people to
  // refresh sent them to a button that returns the same null every time.
  // `undefined` is the only unresolved case, and while auth is still loading
  // even that is too early to call.
  //
  // The badge below and the card read the one value on purpose: the card's row
  // is hidden here precisely because the badge owns the answer, so the two must
  // not be able to disagree.
  const roleKnown = !isAuthLoading && typeof user?.orgRole === 'string'
  const roleAbsent = !isAuthLoading && user?.orgRole === null
  const displayRole = roleKnown ? normalizeRoleForDisplay(user!.orgRole as string) : undefined

  const accountUser = user ? {
    id: user.id,
    email: user.email,
    // Already in display form, so the copied line matches the badge above.
    // Undefined when unconfirmed, which the card pastes as "N/A" — better in a
    // support ticket than a role nobody verified.
    role: displayRole,
    tenantId: user.tenantId,
    tenant_name: user.tenantName,
  } : null

  // Initialize from auth user
  useEffect(() => {
    // The profile fetch failed. Show what the session already knows — a name to
    // split and an email — and mark the form as not reflecting stored data, so
    // a save can't write these stand-ins over the real profile.
    const fallBackToSessionData = () => {
      const name = user?.name || ''
      const [firstName, ...lastParts] = name.split(' ')
      const fallback: ProfileForm = {
        ...EMPTY_PROFILE,
        firstName: firstName || '',
        lastName: lastParts.join(' '),
        email: user?.email || '',
        profilePhoto: user?.avatar || '',
      }
      form.setBaseline(fallback)
      setLoadFailed(true)
    }

    const init = async () => {
      if (!user?.id) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        // Fetch complete user profile from Auth service
        const response = await dashboardApiClient.getCurrentUser()
        
        if (response?.user) {
          const userData = response.user
          
          // Use metadata fields if available, fallback to basic fields
          const firstName = userData.first_name || ''
          const lastName = userData.last_name || ''
          const profilePhoto = userData.avatar_url || user?.avatar || ''
          
          const loaded: ProfileForm = {
            firstName,
            lastName,
            email: userData.email || user?.email || '',
            phone: userData.phone_number || '',
            location: userData.location || '',
            title: userData.job_title || '',
            department: userData.department || '',
            bio: userData.bio || '',
            profilePhoto,
          }
          form.setBaseline(loaded)
          setLoadFailed(false)
        } else {
          fallBackToSessionData()
        }
      } catch (error) {
        console.error('Error loading user profile:', error)
        fallBackToSessionData()
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [user?.id])

  // Handlers
  const handleProfileChange = (field: keyof ProfileForm, value: string) => {
    form.setField(field, value)
  }

  const handleRefreshRole = async () => {
    setIsRefreshing(true)
    try {
      await refreshUser()
    } catch (e) {
      toastError(t('profile.failedToRefresh'), t('common.error'))
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleSave = async () => {
    if (!user?.id || loadFailed) return

    setIsSaving(true)
    try {
      // Send every text field on every save, empty ones included — the Auth
      // service reads an empty string as "clear this field". Mapping empty to
      // `undefined` here dropped the key from the request body, so the service
      // kept the old value and cleared fields reappeared after a refresh.
      const trimmed: ProfileForm = {
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        email: profile.email,
        phone: profile.phone.trim(),
        location: profile.location.trim(),
        title: profile.title.trim(),
        department: profile.department.trim(),
        bio: profile.bio.trim(),
        profilePhoto: profile.profilePhoto.trim(),
      }
      // Show the trimmed values now, before the request goes out. Doing it
      // after would race with anything typed while the save is in flight.
      form.setValues(trimmed)

      await dashboardApiClient.updateUserProfile({
        first_name: trimmed.firstName,
        last_name: trimmed.lastName,
        phone_number: trimmed.phone,
        job_title: trimmed.title,
        department: trimmed.department,
        location: trimmed.location,
        bio: trimmed.bio,
        // No photo here on purpose. It is not a form field: choosing, uploading
        // and removing one each take effect immediately against the Auth
        // service, so there is never a pending photo for Save to commit.
      })

      // Baseline only. The fields stay editable while the request is in flight,
      // so overwriting the values here would throw away anything typed during
      // it; leaving them means the form stays dirty exactly when it should.
      form.markSaved(trimmed)
      toastSuccess(t('profile.settingsSaved'), t('common.success'))

      // Refresh the user context so the nav shows the new name/photo. The save
      // already succeeded, so a failure here must not be reported as a failed
      // save — the next page load picks the values up anyway.
      try {
        await refreshUser()
      } catch (refreshError) {
        console.error('Profile saved, but refreshing the session failed:', refreshError)
      }
    } catch (error) {
      console.error('Error saving user data:', error)
      toastError(error instanceof Error ? error.message : t('profile.failedToSave'), t('common.error'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDiscard = () => {
    // Restore the last state we know the server has. Blanking the fields here
    // would look like a discard but stage an unintended wipe on the next save.
    form.reset()
    setPhotoUploadError(null)
    setPhotoUploadSuccess(false)
    setPhotoRemoveSuccess(false)
  }

  // The nav shows the photo too, so refresh the session after it changes. The
  // change itself already succeeded, so a refresh failure must not be reported
  // as a failed change — the next page load picks it up anyway.
  const refreshPhotoInSession = async () => {
    try {
      await refreshUser()
    } catch (refreshError) {
      console.error('Photo changed, but refreshing the session failed:', refreshError)
    }
  }

  // Removing the photo goes straight to the Auth service rather than waiting for
  // Save, like choosing one does. It is the only path that also deletes the
  // stored file, which is why Save cannot stand in for it.
  const handleRemovePhoto = async () => {
    setIsRemovingPhoto(true)
    setPhotoUploadError(null)
    setPhotoUploadSuccess(false)
    setPhotoRemoveSuccess(false)
    try {
      const token = localStorage.getItem('dashboard:authToken')
      const response = await fetch(`${getServiceApiUrl('auth')}/auth/files/profile-photo`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        // "Nothing to delete" is the outcome we wanted, not a failure. It comes
        // up when the photo is a URL the user pasted into the form and never
        // saved, so it only ever existed in this page's state. Fall through and
        // clear it here. The route also answers NOT_FOUND when the user row
        // itself is missing, so the 400 keeps those two apart.
        if (response.status !== 400 || errorData?.error?.code !== FileUploadErrorCode.NOT_FOUND) {
          throw new Error(errorData?.error?.message || `Remove failed with status ${response.status}`)
        }
      }

      // The photo is gone on the server, so record it as stored rather than as a
      // pending edit. Leaving it in the baseline would put it back the next time
      // the user discards an edit.
      form.commitField('profilePhoto', '')

      await refreshPhotoInSession()

      // Announce it only once the spinner is about to go. The hint shares one
      // slot with the spinner, so setting this before the refresh above would
      // start its three-second timer while it was still hidden — a slow refresh
      // would then clear it before it was ever on screen.
      setPhotoRemoveSuccess(true)
      setTimeout(() => setPhotoRemoveSuccess(false), 3000)
    } catch (error) {
      // The service's own message is untranslated and names nothing the user can
      // act on, so it goes to the console and they get the localized line.
      console.error('Error removing profile photo:', error)
      setPhotoUploadError(t('profile.failedToRemovePhoto'))
    } finally {
      setIsRemovingPhoto(false)
    }
  }

  const handleImageUpload = async (imageUrl: string) => {
    // If it's a blob URL (file upload), we need to upload it to the server
    if (imageUrl.startsWith('blob:')) {
      try {
        setIsUploadingPhoto(true)
        setPhotoUploadError(null)
        setPhotoUploadSuccess(false)
        setPhotoRemoveSuccess(false)
        
        // Convert blob URL to File
        const response = await fetch(imageUrl, { signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) })
        const blob = await response.blob()
        const file = new File([blob], 'profile-photo.jpg', { type: blob.type })
        
        // Upload to Auth service (centralized user data storage)
        const formData = new FormData();
        formData.append('image', file);
        
        const token = localStorage.getItem('dashboard:authToken');
        const uploadResponse = await fetch(`${getServiceApiUrl('auth')}/auth/files/profile-photo`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          throw new Error(errorData?.error?.message || `Upload failed with status ${uploadResponse.status}`);
        }

        const uploadJson = await uploadResponse.json();
        const uploadResult = uploadJson.data;

        if (uploadResult) {
          // Build full URL if needed
          const authBaseUrl = getServiceUrl('auth')
          const fullImageUrl = uploadResult.url.startsWith('http')
            ? uploadResult.url
            : `${authBaseUrl}${uploadResult.url}`
          
          // The upload route already stored this against the user, so record it
          // as the saved state rather than a pending edit.
          form.commitField('profilePhoto', fullImageUrl)
          
          // Clean up the blob URL to prevent memory leaks
          URL.revokeObjectURL(imageUrl)

          await refreshPhotoInSession()

          // Announce it only once the spinner is about to go — see the note in
          // handleRemovePhoto for why this waits for the refresh.
          setPhotoUploadSuccess(true)
          setTimeout(() => setPhotoUploadSuccess(false), 3000)
        } else {
          throw new Error('Upload failed - server response was not successful')
        }
      } catch (error) {
        console.error('Error uploading profile photo:', error)
        const errorMessage = error instanceof Error ? error.message : t('profile.failedToUploadPhoto')
        setPhotoUploadError(errorMessage)
        
        // Don't clean up the blob URL yet in case user wants to retry
      } finally {
        setIsUploadingPhoto(false)
      }
    } else {
      // A plain URL rather than an upload. There is no file to store, but the
      // photo still has to reach the server now — Save no longer carries it.
      try {
        setIsUploadingPhoto(true)
        setPhotoUploadError(null)
        setPhotoUploadSuccess(false)
        setPhotoRemoveSuccess(false)

        const enteredUrl = imageUrl.trim()
        const updated = await dashboardApiClient.updateUserProfile({ avatar_url: enteredUrl })

        // The Auth service keeps a photo address only if it is one it is willing
        // to hand to a browser, and answers with what it actually kept. Taking
        // the typed value on trust instead showed a photo that was gone on the
        // next load.
        if (updated?.avatar_url !== enteredUrl) {
          throw new Error(t('profile.photoUrlNotAccepted'))
        }

        form.commitField('profilePhoto', enteredUrl)

        await refreshPhotoInSession()

        // Announce it only once the spinner is about to go — see the note in
        // handleRemovePhoto for why this waits for the refresh.
        setPhotoUploadSuccess(true)
        setTimeout(() => setPhotoUploadSuccess(false), 3000)
      } catch (error) {
        console.error('Error setting profile photo from a URL:', error)
        setPhotoUploadError(error instanceof Error ? error.message : t('profile.failedToUploadPhoto'))
      } finally {
        setIsUploadingPhoto(false)
      }
    }
    
    setIsImageUploadModalOpen(false)
  }

  return (
    <div className="space-y-6">
      {/* Profile failed to load — say why saving is off */}
      {loadFailed && !isLoading && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-sm">{t('profile.loadFailed')}</span>
        </div>
      )}

      <div className="max-w-2xl space-y-6">
        {/* The suite's standard settings card — a 16px section heading, no
            icon. It was a `Card`, whose `CardTitle` inherits shadcn's stock
            `text-2xl`, making this heading the same size as the page's own h1
            two lines above it.

            The frame and heading paint immediately; only the form body waits
            on the fetch. The identifiers further down are not gated at all —
            they come from the session, so making "copy my IDs into a support
            ticket" wait on the profile would gate the one thing on this tab
            that never needed it. */}
        <FormSection
          title={t('profile.title')}
          // The blocks inside are large — photo, permissions, field grid —
          // so they keep the spacing they had rather than FormSection's
          // tighter `space-y-4` default.
          contentClassName="space-y-6"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* Profile Photo */}
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-primary/15 rounded-full flex items-center justify-center overflow-hidden">
                  {profile.profilePhoto ? (
                    <img src={profile.profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-10 w-10 text-primary" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium">{t('profile.profilePhoto')}</h4>
                  <p className="text-sm text-muted-foreground mb-2">{t('profile.photoHint')}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPhotoUploadError(null)
                        setPhotoUploadSuccess(false)
                        setPhotoRemoveSuccess(false)
                        setIsImageUploadModalOpen(true)
                      }}
                      disabled={isUploadingPhoto || isRemovingPhoto || isSaving || loadFailed}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {profile.profilePhoto ? t('profile.changePhoto') : t('profile.uploadPhoto')}
                    </Button>
                    {profile.profilePhoto && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isUploadingPhoto || isRemovingPhoto || isSaving || loadFailed}
                        onClick={handleRemovePhoto}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('common.remove')}
                      </Button>
                    )}
                  </div>
                  {/* One slot for every photo hint, and it keeps its height when
                      empty. The row is vertically centred, so a hint that
                      appears and disappears would otherwise grow this column
                      and push the photo up and down. */}
                  <div className="mt-2 min-h-[16px] flex items-center gap-2 text-xs">
                    {isUploadingPhoto ? (
                      <>
                        <LoadingSpinner size="small" />
                        <p>{t('profile.uploadingPhoto')}</p>
                      </>
                    ) : isRemovingPhoto ? (
                      <>
                        <LoadingSpinner size="small" />
                        <p>{t('profile.removingPhoto')}</p>
                      </>
                    ) : photoUploadError ? (
                      <p className="text-red-600">{photoUploadError}</p>
                    ) : photoUploadSuccess ? (
                      <p className="text-green-600 flex items-center gap-1">
                        <Check className="h-3 w-3" /> {t('profile.photoUploaded')}
                      </p>
                    ) : photoRemoveSuccess ? (
                      <p className="text-green-600 flex items-center gap-1">
                        <Check className="h-3 w-3" /> {t('profile.photoRemoved')}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Account Role */}
              <div className="p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/15 rounded-full flex items-center justify-center">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-foreground">{t('profile.adminPermissions')}</h4>
                        {/* The description says what the badge means, so when
                            there is no badge it says why instead — and the two
                            reasons for having no badge get different copy, one
                            of which is not a problem to retry. The normal line
                            stands while the role is still resolving; a blank is
                            honest, "could not confirm" is not yet true. */}
                        <p className="text-sm text-muted-foreground">
                          {isAuthLoading || roleKnown
                            ? t('profile.adminPermissionsDesc')
                            : roleAbsent
                              ? t('profile.noOrgRole')
                              : t('profile.roleUnconfirmed')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAuthLoading ? (
                          <span className="h-8 w-20 animate-pulse rounded-full bg-muted" />
                        ) : roleKnown ? (
                          <span className="px-3 py-1.5 bg-primary/15 text-primary text-sm font-medium rounded-full">
                            {displayRole}
                          </span>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRefreshRole}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                          title={t('profile.refreshRole')}
                          disabled={isRefreshing}
                        >
                          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.firstName')}</label>
                  <Input id="firstName" value={profile.firstName} onChange={(v) => handleProfileChange('firstName', v)} placeholder={t('profile.enterFirstName')} leftIcon={User} autoComplete="given-name" />
                </div>
                <div className="space-y-2">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.lastName')}</label>
                  <Input id="lastName" value={profile.lastName} onChange={(v) => handleProfileChange('lastName', v)} placeholder={t('profile.enterLastName')} leftIcon={User} autoComplete="family-name" />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.emailAddress')}</label>
                  <Input id="email" type="email" value={profile.email} onChange={(v) => handleProfileChange('email', v)} placeholder={t('profile.enterEmail')} leftIcon={Mail} disabled className="bg-muted/50" autoComplete="email" />
                  <p className="text-xs text-muted-foreground">{t('profile.emailChangeHint')}</p>
                </div>
                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.phoneNumber')}</label>
                  <Input id="phone" type="tel" value={profile.phone} onChange={(v) => handleProfileChange('phone', v)} placeholder={t('profile.enterPhone')} leftIcon={Phone} autoComplete="tel" />
                </div>
                <div className="space-y-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.jobTitle')}</label>
                  <Input id="title" value={profile.title} onChange={(v) => handleProfileChange('title', v)} placeholder={t('profile.enterJobTitle')} leftIcon={Briefcase} autoComplete="organization-title" />
                </div>
                <div className="space-y-2">
                  <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.department')}</label>
                  <Input id="department" value={profile.department} onChange={(v) => handleProfileChange('department', v)} placeholder={t('profile.enterDepartment')} leftIcon={Building2} autoComplete="organization" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('profile.location')}</label>
                  <Input id="location" value={profile.location} onChange={(v) => handleProfileChange('location', v)} placeholder={t('profile.enterLocation')} leftIcon={MapPin} autoComplete="address-level2" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <TextArea id="bio" value={profile.bio} onChange={(v) => handleProfileChange('bio', v)} placeholder={t('profile.bioPlaceholder')} label={t('profile.bio')} leftIcon={User} minRows={3} maxRows={6} />
                </div>
              </div>
            </>
          )}
        </FormSection>

        {/* The identifiers, read-only, under the fields they describe. They
            are support material — the Copy button exists so they can be
            pasted into a ticket — not a second place to edit an account,
            which is why they sit below the form rather than in a tab of
            their own. Role is hidden: the block above already shows it and
            owns the only control that changes anything about it.

            Company and Workspace are hidden because Dashboard has neither —
            its session user carries only a tenant, so both rows could never
            read anything but "N/A". */}
        <AccountSettingsCard user={accountUser} roleLabel="Org Role" hideRole hideCompany hideWorkspace />

        {/* Inside the capped form container, not after it: the bar should be
            as wide as the thing it commits, and this container is tall enough
            for `position: sticky` to travel within.

            Save no longer carries the photo, but it must not overlap a photo
            change either — both write the same user, and a save landing
            mid-change would report on a profile the server has already moved
            past. That is what the two photo flags in `saveDisabled` are for;
            they are not redundant with `isSaving`. */}
        <SettingsSaveBar
          visible={hasUnsavedChanges}
          onSave={handleSave}
          onDiscard={handleDiscard}
          isSaving={isSaving}
          saveDisabled={isUploadingPhoto || isRemovingPhoto || loadFailed}
          message={t('common.unsavedChanges')}
          saveLabel={t('common.save')}
          savingLabel={t('common.saving')}
          discardLabel={t('common.discard')}
        />
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal isOpen={isImageUploadModalOpen} onClose={() => setIsImageUploadModalOpen(false)} onImageSelect={handleImageUpload} zIndex={99999} />
    </div>
  )
}
